/**
 * 가입 동의 (DEV-07r · D97, 결정 20 · content/legal/consent_screen.md).
 *
 *   제목 → 필수 셋(만 14세 · 약관 · 개인정보, 「보기 ›」 · 펼침 요약) → 모두 동의
 *   → 「동의하고 들어가기」(필수가 다 체크돼야 켜짐). 「나중에」 없음, 뒤로 가기는 로그인 화면으로(이 기기 로그아웃)
 * 문구 · 판은 서버 GET /api/legal/consent(consent_screen.md)에서. 판이 바뀌어 다시 묻는 단골엔 맨 위 안내 한 줄.
 *
 * 태어난 해 고르기는 뺐습니다(사용자 결정 2026-10-16): 만 14세는 「만 14세 이상이에요」 체크로 확인.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ChevronRight, LoaderCircle } from 'lucide-react';
import { TopBar } from '../../components/TopBar.jsx';
import { fetchConsentScreen } from '../../api/legal.js';
import { consentWelcome } from '../../api/counter.js';
import * as authApi from '../../api/auth.js';
import { errorMessageOf } from '../../api/client.js';
import { landingPathOf } from '../../components/RouteGuards.jsx';
import { useSession } from '../../stores/session.js';

function CheckBox({ checked }) {
    const tone = checked ? 'bg-brand' : 'border-[1.5px] border-label-muted bg-surface';
    return (
        <span
            aria-hidden="true"
            className={`flex size-[22px] shrink-0 items-center justify-center rounded-md peer-focus-visible:ring-2 peer-focus-visible:ring-brand ${tone}`}
        >
            {checked ? <Check size={14} strokeWidth={3} className="text-on-brand" /> : null}
        </span>
    );
}

function ConsentItem({ item, checked, expanded, onToggle, onExpand, onView }) {
    return (
        <div className="flex flex-col rounded-xl bg-surface-sunken">
            <div className="flex min-h-11 items-center gap-2 px-3.5 py-2.5">
                <label className="flex flex-1 cursor-pointer items-center gap-2.5 text-[14px] leading-[22px]">
                    <input type="checkbox" checked={checked} onChange={onToggle} className="peer sr-only" />
                    <CheckBox checked={checked} />
                    {item.label}
                </label>
                {item.summary ? (
                    <button
                        type="button"
                        aria-expanded={expanded}
                        aria-label={`요약 ${expanded ? '접기' : '펼치기'}`}
                        onClick={onExpand}
                        className="flex size-8 items-center justify-center text-text-secondary"
                    >
                        <ChevronDown
                            size={18}
                            strokeWidth={1.6}
                            aria-hidden="true"
                            className={expanded ? 'rotate-180' : ''}
                        />
                    </button>
                ) : null}
                {item.doc ? (
                    <button
                        type="button"
                        onClick={onView}
                        className="flex shrink-0 items-center text-[13px] font-semibold text-text-secondary"
                    >
                        보기
                        <ChevronRight size={14} strokeWidth={1.8} aria-hidden="true" />
                    </button>
                ) : null}
            </div>
            {item.summary && expanded ? (
                <p className="m-0 px-3.5 pb-3 pl-[46px] text-[12px] leading-[19px] text-text-secondary">
                    {item.summary}
                </p>
            ) : null}
        </div>
    );
}

export function ConsentPage() {
    const navigate = useNavigate();
    const user = useSession((state) => state.user);
    const setUser = useSession((state) => state.setUser);
    const clearSession = useSession((state) => state.clearSession);
    const [screen, setScreen] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [agreed, setAgreed] = useState({});
    const [open, setOpen] = useState({});
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        fetchConsentScreen({ signal: controller.signal })
            .then(setScreen)
            .catch((failure) => {
                if (!controller.signal.aborted) {
                    setLoadError(errorMessageOf(failure, '동의 화면을 불러오지 못했어요.'));
                }
            });
        return () => controller.abort();
    }, []);

    const items = screen?.items ?? [];
    const allChecked = items.length > 0 && items.every((item) => agreed[item.key]);
    const pending = status === 'pending';
    const ready = allChecked;
    // 예전 판에 동의했던 단골: 약관 · 안내가 바뀌어 다시 묻는 것(DEV-07r 4)
    const renewed = Boolean(user?.consentVersion) && Boolean(screen) && user.consentVersion !== screen.version;

    function toggle(key) {
        setAgreed((current) => ({ ...current, [key]: !current[key] }));
    }

    function toggleAll() {
        const next = !allChecked;
        setAgreed(Object.fromEntries(items.map((item) => [item.key, next])));
    }

    async function submit() {
        if (!ready || pending) {
            return;
        }
        setStatus('pending');
        setError(null);
        try {
            const response = await consentWelcome({ consentVersion: screen.version, agreed });
            setUser(response.user);
            navigate(landingPathOf(response.user), { replace: true });
        } catch (failure) {
            setStatus('idle');
            setError(errorMessageOf(failure, '동의를 기록하지 못했어요. 잠시 뒤 다시 해 주세요.'));
        }
    }

    async function backToLogin() {
        await authApi.logout().catch(() => {
            // 이 기기 세션만 지워도 로그인 화면으로 돌아감
        });
        clearSession();
        navigate('/login', { replace: true });
    }

    return (
        <div className="flex h-dvh flex-col bg-bg">
            <TopBar title="가입 동의" onBack={backToLogin} />
            <main className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pt-5 pb-6">
                {loadError ? (
                    <p role="alert" className="m-0 text-[14px] text-error">
                        {loadError}
                    </p>
                ) : null}
                {screen ? (
                    <>
                        {renewed ? (
                            <p className="m-0 rounded-xl bg-brand-tint px-3.5 py-3 text-[13px] leading-5 text-brand">
                                약관 · 안내가 바뀌었어요. 바뀐 문서를 보고 다시 동의해 주세요.
                            </p>
                        ) : null}
                        <h1 className="m-0 font-serif text-[20px] leading-[30px] font-bold">{screen.title}</h1>
                        {/* 안내 줄은 문서에 있을 때만(2026-10-16 사용자 결정으로 지금은 없음) */}
                        <ul
                            hidden={!screen.notices.length}
                            className="m-0 flex list-none flex-col gap-1.5 rounded-card border border-hairline bg-surface px-4 py-3.5"
                        >
                            {screen.notices.map((notice) => (
                                <li key={notice} className="flex gap-2 text-[14px] leading-[22px]">
                                    <span aria-hidden="true" className="text-label-muted">
                                        ·
                                    </span>
                                    {notice}
                                </li>
                            ))}
                        </ul>

                        <section className="flex flex-col gap-2" aria-label="동의 항목">
                            {items.map((item) => (
                                <ConsentItem
                                    key={item.key}
                                    item={item}
                                    checked={Boolean(agreed[item.key])}
                                    expanded={Boolean(open[item.key])}
                                    onToggle={() => toggle(item.key)}
                                    onExpand={() =>
                                        setOpen((current) => ({ ...current, [item.key]: !current[item.key] }))
                                    }
                                    onView={() => navigate(`/legal/${item.doc}`)}
                                />
                            ))}
                            <label className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-xl border border-hairline bg-surface px-3.5 py-3 text-[14px] leading-[22px] font-semibold">
                                <input
                                    type="checkbox"
                                    checked={allChecked}
                                    onChange={toggleAll}
                                    className="peer sr-only"
                                />
                                <CheckBox checked={allChecked} />
                                {screen.allLabel}
                            </label>
                        </section>

                        <div className="flex-1" />
                        {error ? (
                            <p role="alert" className="m-0 text-center text-[13px] text-error">
                                {error}
                            </p>
                        ) : null}
                        <button
                            type="button"
                            aria-disabled={!ready || pending}
                            onClick={submit}
                            className={`flex h-[52px] items-center justify-center gap-2 rounded-card bg-brand text-[16px] font-bold text-on-brand ${
                                ready ? '' : 'opacity-40'
                            }`}
                        >
                            {pending ? (
                                <LoaderCircle size={18} strokeWidth={1.8} className="animate-spin" aria-hidden="true" />
                            ) : null}
                            {screen.button}
                        </button>
                    </>
                ) : null}
            </main>
        </div>
    );
}
